import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, TrendingUp, Clock, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchAutocomplete = ({ onClose, className = '' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  }, []);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/admin/blog/posts`, {
          params: { published_only: true }
        });
        
        const filtered = response.data.filter(post => 
          post.title?.toLowerCase().includes(query.toLowerCase()) ||
          post.excerpt?.toLowerCase().includes(query.toLowerCase()) ||
          post.category?.toLowerCase().includes(query.toLowerCase())
        ).slice(0, 5);
        
        setResults(filtered);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const saveRecentSearch = (searchTerm) => {
    const updated = [searchTerm, ...recentSearches.filter(s => s !== searchTerm)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const handleSelect = (post) => {
    saveRecentSearch(post.title);
    navigate(`/blog/${post.id}`);
    setIsOpen(false);
    setQuery('');
    if (onClose) onClose();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      saveRecentSearch(query);
      navigate(`/blog?search=${encodeURIComponent(query)}`);
      setIsOpen(false);
      setQuery('');
      if (onClose) onClose();
    }
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <form onSubmit={handleSearch} className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search articles..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="pl-10 pr-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500"
          data-testid="search-autocomplete-input"
        />
        {query && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setQuery('')}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-gray-400 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </form>

      {/* Dropdown */}
      {isOpen && (query.length > 0 || recentSearches.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
          {loading && (
            <div className="p-4 text-center text-gray-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500 mx-auto"></div>
            </div>
          )}

          {/* Search Results */}
          {!loading && results.length > 0 && (
            <div className="py-2">
              <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Results
              </div>
              {results.map((post) => (
                <button
                  key={post.id}
                  onClick={() => handleSelect(post)}
                  className="w-full px-3 py-2 flex items-start gap-3 hover:bg-gray-800 transition-colors text-left"
                  data-testid={`search-result-${post.id}`}
                >
                  {post.image && (
                    <img 
                      src={post.image} 
                      alt="" 
                      className="w-12 h-12 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white text-sm font-medium line-clamp-1">{post.title}</h4>
                    <p className="text-gray-400 text-xs line-clamp-1">{post.excerpt}</p>
                    <span className="text-orange-400 text-xs">{post.category}</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-500 flex-shrink-0 mt-1" />
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="p-4 text-center text-gray-400 text-sm">
              No articles found for "{query}"
            </div>
          )}

          {/* Recent Searches */}
          {!loading && query.length < 2 && recentSearches.length > 0 && (
            <div className="py-2">
              <div className="px-3 py-1.5 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Recent
                </span>
                <button 
                  onClick={clearRecentSearches}
                  className="text-xs text-gray-500 hover:text-orange-400"
                >
                  Clear
                </button>
              </div>
              {recentSearches.map((term, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setQuery(term);
                    inputRef.current?.focus();
                  }}
                  className="w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-800 transition-colors text-left text-sm text-gray-300"
                >
                  <Clock className="h-3 w-3 text-gray-500" />
                  {term}
                </button>
              ))}
            </div>
          )}

          {/* Search All Button */}
          {query.length >= 2 && (
            <div className="border-t border-gray-800 p-2">
              <Button
                onClick={handleSearch}
                variant="ghost"
                className="w-full justify-center text-orange-400 hover:text-orange-300 hover:bg-gray-800"
              >
                <Search className="h-4 w-4 mr-2" />
                Search all for "{query}"
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
